import { useQuery } from "@apollo/client/react";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import { useLocalStorage } from "@scribenote/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "./use-auth";
import { useTemplates } from "./use-templates";
import { SCRIBENOTE_DEFAULTS } from "../components/common/record/constants";
import {
  getTemplateGroupUuid,
  isTemplateGroup,
} from "../components/dashboard/utils";
import { GET_TEMPLATE_GROUPS } from "../graphql/queries/TemplateGroups";

const RecordingJobTypeContext = createContext();

const DEFAULT_JOB_TYPE = {
  value: "medical_record", // The will be a UUID for templates
  name: "Medical Record",
  icon: faSoapNote,
  description:
    "Select 'Medical Record' to generate an automatic SOAP note for your patient.",
};

// For defaults, compare storedValue to option.value;
// For custom templates, compare storedValue to template.uuid.
// For template groups, the value has a "templateGroup:" prefix.
function getMatchingJobType(
  storedValue,
  autoTemplates,
  templateGroups = [],
) {
  if (isTemplateGroup(storedValue)) {
    const groupUuid = getTemplateGroupUuid(storedValue);
    const group = templateGroups.find((g) => g.uuid === groupUuid);
    if (!group) return undefined;
    return {
      value: storedValue,
      name: group.name || "Template Group",
      icon: faLayerGroup,
      isTemplateGroup: true,
    };
  }

  let match = SCRIBENOTE_DEFAULTS.find(
    (option) => option.value === storedValue,
  );
  if (!match && autoTemplates && Array.isArray(autoTemplates)) {
    const templateMatch = autoTemplates.find(
      (template) => template.uuid === storedValue,
    );
    if (templateMatch) {
      match = {
        value: templateMatch.uuid,
        name: templateMatch.name,
        description: templateMatch.description,
        templateType: templateMatch.templateType,
        icon: templateMatch.icon || faFile,
      };
    }
  }
  return match;
}

export const RecordingJobTypeProvider = ({ children }) => {
  const [filterJobType, setFilterJobType] = useLocalStorage(
    "filter-job-type",
    null,
  );
  const auth = useAuth();
  const { allTemplates, teamAndOrgDefaultTemplates } = useTemplates();
  const { releaseGlobalGroupedNotes } = useFlags();
  const { data: templateGroupsData } = useQuery(GET_TEMPLATE_GROUPS, {
    skip: !releaseGlobalGroupedNotes || !auth.isUserAuthenticated,
    fetchPolicy: "cache-and-network",
  });
  const templateGroups = useMemo(
    () => templateGroupsData?.templateGroups || [],
    [templateGroupsData?.templateGroups],
  );

  // All templates the user can select from: owned/shared + team/org defaults.
  // Team and org defaults come from separate queries and may not appear in
  // allTemplates, so we combine both lists for UUID resolution.
  const allSelectableTemplates = useMemo(
    () => [
      ...(allTemplates || []),
      ...(teamAndOrgDefaultTemplates || []),
    ],
    [allTemplates, teamAndOrgDefaultTemplates],
  );

  // Derive the job type from localStorage + loaded templates.
  // Recalculates when templates load/change, resolving template UUIDs
  // that can't be matched until the queries complete.
  const storageJobType = useMemo(() => {
    const storedValue = localStorage.getItem("recordingJobType");
    if (storedValue) {
      return (
        getMatchingJobType(
          storedValue,
          allSelectableTemplates,
          templateGroups,
        ) || DEFAULT_JOB_TYPE
      );
    }
    return DEFAULT_JOB_TYPE;
  }, [allSelectableTemplates, templateGroups]);

  // Tracks explicit user selections within the session. When null, we
  // fall back to the storage-derived value above.
  const [userOverride, setUserOverride] = useState(null);

  // Validate the override against the full selectable list (owned + team/org
  // defaults). Falls back to storageJobType when no override is set or the
  // override UUID is not found in allSelectableTemplates.
  const selectedJobType = useMemo(() => {
    if (!userOverride) return storageJobType;
    const stillValid = getMatchingJobType(
      userOverride.value,
      allSelectableTemplates,
      templateGroups,
    );
    return stillValid ? userOverride : storageJobType;
  }, [
    userOverride,
    allSelectableTemplates,
    templateGroups,
    storageJobType,
  ]);

  const setSelectedJobType = (jobType) => {
    setUserOverride(jobType);
    if (jobType) {
      localStorage.setItem("recordingJobType", jobType.value);
    }
  };

  const resetSelectedJobType = () =>
    setSelectedJobType(DEFAULT_JOB_TYPE);

  // Reset job type when the user becomes logged out
  useEffect(() => {
    if (!auth.isUserAuthenticated) {
      setFilterJobType(null);
    }
  }, [auth.isUserAuthenticated, setFilterJobType]);

  return (
    <RecordingJobTypeContext.Provider
      value={{
        selectedJobType,
        setSelectedJobType,
        resetSelectedJobType,
        filterJobType,
        setFilterJobType,
      }}
    >
      {children}
    </RecordingJobTypeContext.Provider>
  );
};

export const useRecordingJobType = () => {
  const context = useContext(RecordingJobTypeContext);
  if (!context) {
    throw new Error(
      "useRecordingJobType must be used within a RecordingJobTypeProvider",
    );
  }
  return context;
};
